import React from 'react';
import styled from 'styled-components';

interface BaseModalProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export const ModalHeader: React.FC<BaseModalProps & { sticky?: boolean; noBorder?: boolean }> = ({ 
  children, 
  sticky, 
  noBorder,
  className = '',
  style,
  ...props 
}) => (
  <div 
    className={`modal-header ${sticky ? 'sticky-top bg-white' : ''} ${noBorder ? 'border-0' : ''} ${className}`} 
    style={{ zIndex: 10, ...style }}
    {...props}
  >
    {children}
  </div>
);

export const ModalTitle = styled.h5.attrs({ className: 'modal-title' })`
  font-weight: 800;
  color: #2d3748;
  flex: 1;
  text-align: center;
`;

export const CloseButton: React.FC<{ onClick: () => void; children?: React.ReactNode; color?: string } & React.ButtonHTMLAttributes<HTMLButtonElement>> = ({ 
  onClick, 
  children,
  color,
  className = '',
  style,
  ...props 
}) => {
  const mergedStyle = { 
    cursor: 'pointer', 
    color: color || 'inherit',
    ...style 
  };

  if (children) {
    return (
      <button 
        type="button" 
        className={`border-0 bg-transparent p-0 d-flex align-items-center justify-content-center ${className}`} 
        onClick={onClick} 
        style={mergedStyle}
        {...props}
      >
        {children}
      </button>
    );
  }
  return (
    <button 
      type="button" 
      className={`btn-close ${className}`} 
      onClick={onClick} 
      aria-label="Close"
      style={{ fontSize: '0.8rem', ...style }}
      {...props}
    ></button>
  );
};

export const BackButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #2d3748;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  width: 32px;
  height: 32px;
  transition: transform 0.2s;

  &:hover {
    transform: translateX(-3px);
  }
`;

export const ModalBody: React.FC<BaseModalProps & { padding?: string }> = ({ 
  children, 
  padding,
  className = '',
  style,
  ...props
}) => (
  <div 
    className={`modal-body ${className}`} 
    style={{ padding: padding || '1.5rem', ...style }}
    {...props}
  >
    {children}
  </div>
);

export const ModalFooter: React.FC<BaseModalProps & { sticky?: boolean; mobileFlexRow?: boolean }> = ({ 
  children, 
  sticky,
  mobileFlexRow,
  className = '',
  style,
  ...props
}) => (
  <div 
    className={`modal-footer ${sticky ? 'sticky-bottom bg-white' : ''} ${mobileFlexRow ? 'flex-row' : 'flex-column'} border-top-1 ${className}`} 
    style={{ zIndex: 10, ...style }}
    {...props}
  >
    {children}
  </div>
);

export const ModalOverlay = styled.div.attrs({ className: 'modal fade show', role: 'dialog', tabIndex: -1 })<{ zIndex?: number }>`
  display: block;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: ${props => props.zIndex || 1050};
`;

export const ModalContent: React.FC<BaseModalProps & { maxWidth?: string; noPadding?: boolean }> = ({ 
  children, 
  maxWidth,
  className = '',
  style,
  ...props
}) => (
  <div 
    className={`modal-dialog modal-dialog-centered modal-dialog-scrollable ${className}`} 
    style={{ 
      maxWidth: maxWidth || '500px',
      margin: window.innerWidth <= 480 ? '0' : '1.75rem auto',
      ...style
    }} 
    {...props}
  >
    <div 
      className="modal-content" 
      style={{ 
        borderRadius: window.innerWidth <= 480 ? '0' : '20px', 
        border: 'none', 
        height: window.innerWidth <= 480 ? '100dvh' : 'auto',
        maxHeight: window.innerWidth <= 480 ? '100dvh' : '90vh',
        overflow: 'hidden'
      }}
    >
      {children}
    </div>
  </div>
);
